// ═══════════════════════════════════════════════════════════════════════════
// targetSheetPrint.js — the Version B target sheet, rendered for print.
//
// Kevin, 2026-08-24: "what if we created a digital target sheet on our app
// that we could then print and put in our target book? ... I would want to
// match our target sheet format exactly." Version B is the newer of the two
// forms in the book and the one he picked.
//
// ── WHY THE FORM IS THE FIX, NOT JUST THE FILING ──────────────────────────
//
// Five of the ten working tickets photographed on 2026-08-24 recorded NO
// DELIVERY POINT, and the delivery point is the single field that decides the
// basis. Priced across the points each could have meant, the missing field was
// worth up to 34c — $3,740 on Zeliff's 11,000 bu alone. `+15` does not
// identify it either: Batavia, LeRoy and Avon were ALL about +0.15 in March
// and now sit 34c apart.
//
// So the digital sheet closes two holes the paper cannot:
//   1. DELIVERY POINT IS REQUIRED. No ticket without one.
//   2. THE FUTURES TARGET IS DERIVED, NOT TYPED. cash - current basis, off
//      grain_basis_board, at the moment of writing — which kills the
//      entry-time mismatch as well as the drift. Corcoran's ticket says April
//      on K27 while the board pairs April with H27; a form that reads the
//      board cannot write that pairing down.
//
// ── AND IT PRINTS ITS OWN PROVENANCE, WHICH PAPER NEVER DID ───────────────
//
// Every sheet carries the basis it used, the board version and the sheet date
// underneath the computed target. A number on a page with no source is exactly
// what this whole exercise has been about: months later nobody could tell
// whether +15 was Batavia's or LeRoy's, and that ambiguity was worth $3,740.
//
// PURE — returns an HTML string. No fetch, no React, no DOM. The caller opens
// a window and prints it, the pattern the LRP monthly summary already uses.
// ═══════════════════════════════════════════════════════════════════════════

// The Version B field vocabulary, transcribed from the printed form. Order and
// wording are the form's, not ours — "match our target sheet format exactly".
export const SHEET_VERSION = "B";
export const STRUCTURES = ["Priced", "Basis", "Cash Target", "HTA"];
export const COMMODITIES = ["Corn", "Soybeans", "Plenish", "Wheat", "Oats", "Rye", "Barley"];
export const DELIVERY_POINTS = ["Avon", "Lakeville", "Batavia", "Leroy", "Geneva"];
export const MARKET_CROPS = ["Corn", "Beans", "Wheat"];
export const TARGET_ROWS = 4;   // the CBOT / cash / bushels block prints four

const esc = (s) =>
  String(s == null ? "" : s).replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

const money = (n) => (n == null || !Number.isFinite(Number(n)) ? "" : "$" + Number(n).toFixed(2));
// Basis prints the way the form is written by hand: +15 / -06, not +0.15.
export const basisCents = (n) =>
  n == null || !Number.isFinite(Number(n))
    ? ""
    : (Number(n) >= 0 ? "+" : "-") + String(Math.round(Math.abs(Number(n)) * 100)).padStart(2, "0");

const box = (checked, label) =>
  `<span class="cb"><span class="bx">${checked ? "&#10005;" : ""}</span>${esc(label)}</span>`;
const line = (val, cls = "") => `<span class="ln ${cls}">${esc(val ?? "")}</span>`;

// ── one sheet ──────────────────────────────────────────────────────────────
// `ticket` may be null / {} — a BLANK sheet is a first-class output, because
// the pad has to be printable for the times somebody writes one by hand.
export function renderTargetSheet(ticket, opts = {}) {
  const t = ticket || {};
  const rungs = (t.rungs && t.rungs.length ? t.rungs : [{}]).slice(0, TARGET_ROWS);
  const pt = String(t.deliveryPoint || "");
  const otherPoint = pt && !DELIVERY_POINTS.some((p) => p.toLowerCase() === pt.toLowerCase()) ? pt : "";

  const rungRows = Array.from({ length: TARGET_ROWS }, (_, i) => {
    const r = rungs[i] || {};
    return `<tr>
      <td class="c">${line(money(r.futuresTarget), "sm")}</td><td class="x">,</td>
      <td class="c">${line(money(r.cashTarget), "sm")}</td><td class="lbl">Cash</td>
      <td class="c">${line(r.bushels == null ? "" : Number(r.bushels).toLocaleString(), "sm")}</td>
    </tr>`;
  }).join("");

  // The provenance strip. Prints only when the sheet was computed from a board
  // — a hand-written blank must not carry a basis it did not use.
  const prov = opts.basisSource
    ? `<div class="prov">Basis ${esc(basisCents(opts.basis))} &middot; ${esc(opts.basisSource)}
       ${opts.sheetVersion ? "&middot; board " + esc(opts.sheetVersion) : ""}
       ${opts.sheetDate ? "&middot; " + esc(opts.sheetDate) : ""}
       &middot; target = cash &minus; basis</div>`
    : "";

  return `<div class="sheet">
  <div class="hd">
    <div class="hd-l">
      <div><b>Buyer:</b> ${line(t.buyer)}</div>
      <div><b>Date:</b> ${line(t.enteredOn)}</div>
    </div>
    <div class="hd-r">
      <div><b>Name:</b> ${line(t.farmerName, "wide")}</div>
      <div class="blank"></div><div class="blank"></div>
    </div>
  </div>

  <div class="row big">${box(t.direction !== "sales", "Purchase")}${box(t.direction === "sales", "Sales")}</div>
  <div class="row">${STRUCTURES.map((s) =>
      box(String(t.structure || "Cash Target").toLowerCase() === s.toLowerCase(), s)).join("")}</div>
  <div class="row">${COMMODITIES.map((c) =>
      box(String(t.commodity || "").toLowerCase() === c.toLowerCase(), c)).join("")}</div>

  <div class="row"><b>Contract #:</b> ${line(t.contractNumber, "wide")}</div>
  <div class="row two">
    <div><b>Bushels :</b> ${line(t.bushels == null ? "" : Number(t.bushels).toLocaleString())}</div>
    <div><b>Shipment Period :</b> ${line(t.deliveryWindow)}</div>
  </div>
  <div class="row two">
    <div><b>Price:</b> ${line(t.price)}</div>
    <div><b>Basis:</b> ${line(basisCents(opts.basis ?? t.basisAtEntry), "sm")}
         &nbsp;&nbsp;<b>CME Month:</b> ${line(t.futuresContract, "sm")}</div>
  </div>
  <div class="row"><b>Delay Payment:</b> ${line(t.delayPayment, "wide")}</div>

  <div class="row">${box(t.bidStructure === "fob_point", "FOB Point")}${box(t.bidStructure === "pickup", "Pickup / FOB Farm")}</div>
  <div class="deliver">
    <div class="dl">${box(t.bidStructure === "delivered" || !t.bidStructure, "Delivered to:")}</div>
    <div class="dr">
      <div>${DELIVERY_POINTS.slice(0, 3).map((p) => box(p.toLowerCase() === pt.toLowerCase(), p)).join("")}</div>
      <div>${DELIVERY_POINTS.slice(3).map((p) => box(p.toLowerCase() === pt.toLowerCase(), p)).join("")}</div>
      <div>${box(!!otherPoint, "Other:")}${line(otherPoint, "wide")}</div>
    </div>
  </div>

  <div class="row tn-l"><b>TRUCKING NOTES:</b></div>
  <div class="tn"><div class="tn-c">${esc(t.truckingNotes || "")}</div>
    <div class="tn-c"></div><div class="tn-c"></div><div class="tn-c"></div></div>

  <div class="mkt">
    <div class="mkt-l">
      <div class="mrow"><b>Market</b> &nbsp;&nbsp; <b>Target @:</b> ${line(money(rungs[0] && rungs[0].futuresTarget), "xs")}
           &nbsp;&nbsp; <b>Stop @:</b> ${line(t.stopAt, "xs")}</div>
      <div class="mrow sub">${box(t.side === "buy", "Buy")}${box(t.side !== "buy", "Sell")}</div>
      <div class="mrow"><b>BU:</b> ${line(t.bushels == null ? "" : Number(t.bushels).toLocaleString())}</div>
      <div class="mrow"><b>Fut. Month:</b> ${line(t.futuresContract)}</div>
      <div class="mrow sub">${MARKET_CROPS.map((c) =>
          box(String(t.commodity || "").toLowerCase().startsWith(c.toLowerCase().slice(0, 4)), c)).join("")}</div>
      <div class="mrow"><b>Fill:</b> ${line(t.fill, "wide")}</div>
    </div>
    <div class="mkt-r">
      <table><thead><tr><th colspan="4">CBOT</th><th class="bu">Bushels</th></tr></thead>
      <tbody>${rungRows}</tbody></table>
    </div>
  </div>
  ${prov}
</div>`;
}

// Full printable document: several sheets, one per page.
export function renderTargetSheetDocument(tickets, opts = {}) {
  const list = Array.isArray(tickets) && tickets.length ? tickets : [null];
  const sheets = list
    .map((t) => renderTargetSheet(t, (t && t.provenance) || opts))
    .join('<div class="pb"></div>');
  return `<!doctype html><html><head><meta charset="utf-8"/>
<title>Target Sheet</title><style>
  @page { size: letter portrait; margin: 0.4in; }
  body { font: 12.5pt/1.5 Arial, Helvetica, sans-serif; color: #000; margin: 0; }
  /* THE SHEET FILLS THE PAGE. The first cut sat in the top 60% and read as a
     cramped web form rather than the pad it replaces — 0.4in margins on letter
     leave 7.7 x 10.2in, so the sheet is sized to that and the blocks grow into
     it. Kevin, 2026-08-24: "It looks a little rough. It needs to be a one page
     print just like our current price sheet." */
  .sheet { page-break-inside: avoid; height: 10.2in; display: flex;
           flex-direction: column; box-sizing: border-box; }
  .pb { page-break-after: always; }
  .row { margin: 0 0 11px; }
  .row.big { font-size: 14pt; font-weight: 700; margin-bottom: 13px; }
  .row.two { display: flex; gap: 26px; }
  .row.two > div { flex: 1; }
  .hd { display: flex; gap: 28px; margin-bottom: 16px; }
  .hd-l { width: 32%; } .hd-l > div { margin-bottom: 10px; }
  .hd-r { flex: 1; }
  .blank { border-bottom: 1px solid #000; height: 26px; }
  .ln { display: inline-block; min-width: 110px; border-bottom: 1px solid #000;
        padding: 0 5px; font-weight: 700; }
  .ln.wide { min-width: 270px; } .ln.sm { min-width: 74px; } .ln.xs { min-width: 58px; }
  .cb { margin-right: 15px; white-space: nowrap; }
  .bx { display: inline-block; width: 13px; height: 13px; border: 1.2px solid #000;
        margin-right: 5px; text-align: center; line-height: 12px; font-size: 12px; }
  /* Delivery points STACK, the way the printed form lays them out — three,
     then two, then Other. This is the field five paper tickets left blank. */
  .deliver { display: flex; gap: 18px; margin-bottom: 12px; }
  .deliver .dr > div { margin-bottom: 8px; }
  .deliver .dl { width: 34%; }
  .tn-l { margin-bottom: 4px; }
  /* The printed trucking box is a four-column grid, not a plain rectangle. */
  .tn { display: flex; border: 1px solid #000; flex: 1; min-height: 1.05in; margin-bottom: 12px; }
  .tn-c { flex: 1; padding: 5px; border-right: 1px solid #000; }
  .tn-c:last-child { border-right: 0; }
  .mkt { display: flex; border: 1.2px solid #000; margin-top: auto; }
  .mkt-l { flex: 1; padding: 9px 11px; border-right: 1.2px solid #000; min-width: 0; }
  .mkt-l .mrow { margin-bottom: 9px; white-space: nowrap; }
  .mkt-l .mrow:last-child { margin-bottom: 0; }
  .mkt-l .sub { padding-left: 30px; }
  .mkt-r { width: 46%; padding: 9px 11px; }
  .mkt-r table { width: 100%; border-collapse: collapse; }
  .mkt-r th { text-align: left; padding-bottom: 6px; font-size: 12.5pt; }
  .mkt-r th.bu { text-align: right; }
  .mkt-r td { padding: 5px 0; } .mkt-r td.x { width: 9px; }
  .mkt-r td.lbl { padding-left: 8px; padding-right: 10px; }
  .mkt-r td.c { text-align: right; }
  /* Provenance is the one thing the paper form never carried. Small, but it
     travels with the sheet into the book — see the header note. */
  .prov { margin-top: 8px; font-size: 8.5pt; color: #333; border-top: 1px dotted #999;
          padding-top: 5px; }
  @media print { .prov { color: #000; } }
</style></head><body>${sheets}</body></html>`;
}
